import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Phone, Home } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import EstimationBanner from '../../components/EstimationBanner';
import OptimizedImage from '../../components/OptimizedImage';

import SEO from '../../components/SEO';

const ServiceMaison = () => {
  const advantages = [
    'Intervention rapide sous 48-72h',
    'Tri sélectif de tous les biens',
    'Réutilisation des objets en état d\'usage',
    'Balayage après intervention',
    'Devis gratuit et sans engagement',
    'Équipe professionnelle et assurée'
  ];

  const situations = [
    {
      title: 'Avant déménagement',
      description: 'Vous déménagez et souhaitez vous débarrasser des meubles et objets que vous ne souhaitez pas emporter.'
    },
    {
      title: 'Avant vente immobilière',
      description: 'Votre bien doit être vidé avant la mise en vente ou la remise des clés au nouveau propriétaire.'
    },
    {
      title: 'Avant travaux de rénovation',
      description: 'Vous prévoyez des travaux et devez vider entièrement la maison pour les artisans.'
    },
    {
      title: 'Désencombrement progressif',
      description: 'Vous souhaitez retrouver de l\'espace et vous débarrasser d\'objets accumulés au fil des années.'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Débarras de maison en Haute-Savoie et Savoie | DEBECO"
        description="Service complet de vide-maison à Annecy et en Savoie. Intervention rapide sous 48-72h, tri sélectif et recyclage. Devis gratuit."
        canonical="/services/maison"
        ogImage="/images/maison.jpg"
        jsonLd={[
          {"@context":"https://schema.org","@type":"Service","name":"Débarras de maison","description":"Service complet de vide-maison, du grenier à la cave. Tri, recyclage et évacuation de tous les encombrants.","provider":{"@type":"LocalBusiness","name":"DEBECO","telephone":"+33451401617","address":{"@type":"PostalAddress","addressLocality":"Annecy","postalCode":"74600","addressCountry":"FR"}},"areaServed":[{"@type":"AdministrativeArea","name":"Haute-Savoie"},{"@type":"AdministrativeArea","name":"Savoie"},{"@type":"AdministrativeArea","name":"Ain"}]},
          {"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"Accueil","item":"https://debeco.fr/"},{"@type":"ListItem","position":2,"name":"Services","item":"https://debeco.fr/services"},{"@type":"ListItem","position":3,"name":"Débarras de maison"}]}
        ]}
      />
      {/* Hero */}
      <section className="hero-banner text-white py-16">
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Débarras de mobilier et vide maison</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Service complet de vide-maison à Annecy, Genevois et Savoie
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
              {/* Image */}
              <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
                <OptimizedImage 
                  src="/images/maison.jpg"
                  webpSrc="/images/maison.webp"
                  alt="Intérieur d'une maison en cours de débarras par l'équipe DEBECO, pièce avec mobilier à évacuer"
                  className="w-full h-full object-cover"
                  width="1200"
                  height="806"
                  loading="eager"
                  data-testid="service-maison-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 to-transparent"></div>
                <div className="absolute bottom-6 left-6">
                  <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center shadow-lg">
                    <Home className="w-10 h-10 text-blue-900" />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div>
                <h2 className="text-3xl font-bold text-blue-900 mb-6">Vide-maison complet et rapide</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Notre service de débarras de maison est conçu pour vous accompagner dans toutes les situations : déménagement, vente immobilière, succession, ou simple besoin de désencombrement. Nous intervenons rapidement pour vider intégralement votre maison, du grenier à la cave.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  Notre équipe professionnelle trie, recycle ce qui peut l'être et évacue le reste vers des filières agréées. Vous n'avez rien à faire : nous nous occupons de tout, du premier coup de téléphone jusqu'au nettoyage final.
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/contact">
                    <Button className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white shadow-lg w-full sm:w-auto">
                      Demander un devis gratuit
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <a href="tel:0451401617">
                    <Button variant="outline" className="border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white w-full sm:w-auto">
                      <Phone className="mr-2 w-4 h-4" />
                      04 51 40 16 17
                    </Button>
                  </a>
                </div>
              </div>
            </div>

            {/* Advantages */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">Les avantages de notre service</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {advantages.map((advantage, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{advantage}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Situations */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">Dans quels cas nous contacter ?</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {situations.map((situation, index) => (
                  <Card key={index} className="border-2 hover:border-emerald-500 hover:shadow-xl transition-all">
                    <CardContent className="p-6">
                      <h3 className="font-bold text-xl text-blue-900 mb-3">{situation.title}</h3>
                      <p className="text-gray-600">{situation.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Estimation Banner */}
      <EstimationBanner />

      {/* Nos interventions par ville */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-blue-900 mb-4 text-center">Nos interventions par ville</h2>
            <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto">
              Découvrez nos pages dédiées au débarras de maison dans les principales villes de notre zone d'intervention.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <Link to="/debarras-maison-annecy" className="group">
                <Card className="border-2 hover:border-emerald-500 hover:shadow-xl transition-all h-full">
                  <CardContent className="p-5 flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-100 transition-colors">
                      <Home className="w-5 h-5 text-blue-900 group-hover:text-emerald-700" />
                    </div>
                    <div>
                      <h3 className="font-bold text-blue-900 group-hover:text-emerald-700 transition-colors">Annecy</h3>
                      <p className="text-gray-500 text-sm">Haute-Savoie (74)</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-400 ml-auto group-hover:text-emerald-600 transition-colors" />
                  </CardContent>
                </Card>
              </Link>
              <Link to="/vide-maison-annecy" className="group">
                <Card className="border-2 hover:border-emerald-500 hover:shadow-xl transition-all h-full">
                  <CardContent className="p-5 flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-100 transition-colors">
                      <Home className="w-5 h-5 text-blue-900 group-hover:text-emerald-700" />
                    </div>
                    <div>
                      <h3 className="font-bold text-blue-900 group-hover:text-emerald-700 transition-colors">Vide-maison à Annecy</h3>
                      <p className="text-gray-500 text-sm">Vidage intégral avant vente ou travaux</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-400 ml-auto group-hover:text-emerald-600 transition-colors" />
                  </CardContent>
                </Card>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Process */}
            <Card className="border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-green-50">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-blue-900 mb-6 text-center">Notre méthode de travail</h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  {[
                    { step: '1', title: 'Contact', desc: 'Vous nous appelez ou remplissez le formulaire' },
                    { step: '2', title: 'Visite & Devis', desc: 'Évaluation gratuite sur place' },
                    { step: '3', title: 'Intervention', desc: 'Débarras complet sous 48-72h' },
                    { step: '4', title: 'Nettoyage', desc: 'Remise en état de votre bien' }
                  ].map((item) => (
                    <div key={item.step} className="text-center">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-900 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                        <span className="text-xl font-bold text-white">{item.step}</span>
                      </div>
                      <h3 className="font-bold text-blue-900 mb-1">{item.title}</h3>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Maillage interne */}
      <section className="py-12 bg-gray-50" data-testid="maillage-interne">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-blue-900 mb-6">Nos autres services de débarras</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/services/succession" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Débarras après succession</p>
              <p className="text-sm text-gray-600 mt-1">Accompagnement respectueux pour vider un bien hérité</p>
            </Link>
            <Link to="/blog/prix-debarras-maison-annecy" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Prix d'un débarras de maison à Annecy</p>
              <p className="text-sm text-gray-600 mt-1">Guide tarifaire complet pour estimer votre budget</p>
            </Link>
            <Link to="/devis" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Estimation gratuite en ligne</p>
              <p className="text-sm text-gray-600 mt-1">Obtenez un devis en quelques minutes</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-emerald-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Besoin d'un débarras de maison ?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Contactez-nous pour un devis gratuit et une intervention rapide dans toute la région
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100 shadow-xl text-lg px-8 py-6 w-full sm:w-auto">
                Demander un devis
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:0451401617">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6 w-full sm:w-auto">
                <Phone className="mr-2 w-5 h-5" />
                04 51 40 16 17
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceMaison;
